import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import { useT } from "../../../../core/i18n";
import Api from "../../../../core/data/redux/utils/apiCall";
import {
  EncashmentMethodOption,
  LeaveEncashmentFormData,
  LeaveTypeOption,
  clearFormDraft,
  fetchEncashmentMethods,
  fetchLeaveTypes,
  getLoggedInEmpId,
  leaveTypeLabel,
  readFormDraft,
  saveFormDraft,
  uniqueLeaveTypes,
} from "./leaveEncashment.service";

const emptyForm = (): LeaveEncashmentFormData => ({
  leaveType: "",
  method: "",
  days: "",
  note: "",
  ReferenceNumber: "",
  LeaveTypeCode: "",
  LeaveEncaMeth: "",
  LeaveTypeText: "",
  LeaveEncaMethText: "",
});

const CreateLeaveEncashment: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const routes = all_routes;
  const draftItem = (location.state as any)?.draftItem ?? null;

  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeOption[]>([]);
  const [methods, setMethods] = useState<EncashmentMethodOption[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [loadingMethods, setLoadingMethods] = useState(false);
  const [form, setForm] = useState<LeaveEncashmentFormData>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const leaveOptions = useMemo(() => uniqueLeaveTypes(leaveTypes), [leaveTypes]);

  const label = (key: string, fallback: string) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const loadMethods = async (leaveTypeCode: string, matchDraft: boolean) => {
    setLoadingMethods(true);
    try {
      const list = await fetchEncashmentMethods(leaveTypeCode, getLoggedInEmpId());
      setMethods(list);
      if (matchDraft && draftItem) {
        const draftMethodCode = draftItem.CompMethod ?? draftItem.LeaveEncaMeth ?? "";
        const found = list.find((m) => m.EncashmentMethod === draftMethodCode);
        if (found) {
          setForm((prev) => ({
            ...prev,
            method: found.EncashmentMethodText,
            LeaveEncaMeth: found.EncashmentMethod,
            LeaveEncaMethText: found.EncashmentMethodText,
          }));
        }
      }
    } catch (err: any) {
      setMethods([]);
      toast.error(Api.getErrorMessage(err) || "Failed to load encashment methods");
    } finally {
      setLoadingMethods(false);
    }
  };

  useEffect(() => {
    const saved = readFormDraft();
    const load = async () => {
      setLoadingTypes(true);
      try {
        const list = await fetchLeaveTypes(getLoggedInEmpId());
        setLeaveTypes(list);
        if (saved?.LeaveTypeCode) {
          setForm(saved);
          await loadMethods(saved.LeaveTypeCode, false);
        } else if (draftItem) {
          const draftLeaveTypeCode = draftItem.QuotaType ?? draftItem.LeaveTypeCode ?? "";
          const found = list.find((lt) => lt.LeaveTypeCode === draftLeaveTypeCode);
          setForm((prev) => ({
            ...prev,
            ReferenceNumber: String(draftItem?.ReferenceNumber ?? draftItem?.id ?? draftItem?.Id ?? ""),
            days: String(draftItem?.QuotaNumber ?? ""),
            note: draftItem?.Comment ?? "",
            leaveType: found ? leaveTypeLabel(found) : prev.leaveType,
            LeaveTypeCode: found?.LeaveTypeCode || "",
            LeaveTypeText: found?.LeaveTypeText || "",
          }));
          if (found) await loadMethods(found.LeaveTypeCode, true);
        }
      } catch (err: any) {
        toast.error(Api.getErrorMessage(err) || "Failed to load leave types");
      } finally {
        setLoadingTypes(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLeaveTypeChange = async (labelText: string) => {
    const found = leaveOptions.find((lt) => leaveTypeLabel(lt) === labelText);
    setForm((prev) => ({
      ...prev,
      leaveType: labelText,
      method: "",
      LeaveEncaMeth: "",
      LeaveEncaMethText: "",
      LeaveTypeCode: found?.LeaveTypeCode || "",
      LeaveTypeText: found?.LeaveTypeText || labelText,
    }));
    setMethods([]);
    setErrors((prev) => ({ ...prev, leaveType: "", method: "" }));
    if (found) await loadMethods(found.LeaveTypeCode, false);
  };

  const onMethodChange = (labelText: string) => {
    const found = methods.find((m) => m.EncashmentMethodText === labelText);
    setForm((prev) => ({
      ...prev,
      method: labelText,
      LeaveEncaMeth: found?.EncashmentMethod || "",
      LeaveEncaMethText: found?.EncashmentMethodText || labelText,
    }));
    setErrors((prev) => ({ ...prev, method: "" }));
  };

  const validate = () => {
    const next: Record<string, string> = {};
    if (!form.leaveType.trim()) next.leaveType = "Leave type is required";
    if (!form.method.trim()) next.method = "Method is required";
    const days = Number(form.days);
    if (!form.days.trim() || Number.isNaN(days)) next.days = "Enter valid days";
    else if (days < 1) next.days = "Days required";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const instruction = label(
    "leave_encashment_instruction",
    "Please choose as mentioned below:\n\n1. Leave Type: Earned Leave - Encashable (mentioned on TOP of the list)\n\n2. Leave Encashment Method: Earned Leave - Encashable (mentioned on TOP of the list)"
  ).split("\\n").join("\n");

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div>
            <h3 className="page-title mb-1">{label("employee_leave_encashment", "Employee Leave Encashment")}</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.leaveEncashment.list}>{label("leave_encashment_overview", "Leave Encashment Overview")}</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">{label("employee_leave_encashment", "Employee Leave Encashment")}</li>
              </ol>
            </nav>
          </div>
          <button type="button" className="btn btn-light" onClick={() => navigate(-1)}>
            <i className="ti ti-arrow-left me-1"></i>
            {t("back")}
          </button>
        </div>

        {loadingTypes ? (
          <div className="text-center py-5"><div className="spinner-border text-danger" role="status" /></div>
        ) : (
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="alert alert-light border-start border-danger border-4">
                <div className="fw-bold mb-1">{label("Instruction", "Instruction")}</div>
                <div style={{ whiteSpace: "pre-line" }}>{instruction}</div>
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">{label("leave_type", "Leave Type")} <span className="text-danger">*</span></label>
                <select className="form-select" value={form.leaveType} onChange={(e) => onLeaveTypeChange(e.target.value)}>
                  <option value="">{t("select_option")}</option>
                  {leaveOptions.map((lt) => {
                    const text = leaveTypeLabel(lt);
                    return <option key={`${lt.LeaveTypeCode}-${lt.LeaveEncaMeth}`} value={text}>{text}</option>;
                  })}
                </select>
                {errors.leaveType && <div className="text-danger small mt-1">{errors.leaveType}</div>}
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">{label("leave_encashment_method", "Leave Encashment Method")} <span className="text-danger">*</span></label>
                <select className="form-select" value={form.method} disabled={!form.LeaveTypeCode || loadingMethods} onChange={(e) => onMethodChange(e.target.value)}>
                  <option value="">{t("select_option")}</option>
                  {methods.map((method) => (
                    <option key={method.EncashmentMethod} value={method.EncashmentMethodText}>{method.EncashmentMethodText}</option>
                  ))}
                </select>
                {errors.method && <div className="text-danger small mt-1">{errors.method}</div>}
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">{t("days_to_be_encashed")} <span className="text-danger">*</span></label>
                <input type="number" min={1} className="form-control" value={form.days} onChange={(e) => { setForm((prev) => ({ ...prev, days: e.target.value })); setErrors((prev) => ({ ...prev, days: "" })); }} />
                {errors.days && <div className="text-danger small mt-1">{errors.days}</div>}
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">{label("note_for_approver", "Note for Approver")}</label>
                <textarea className="form-control" rows={3} value={form.note} onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))} />
              </div>
              <div className="d-flex justify-content-end gap-2">
                <button type="button" className="btn btn-light" onClick={() => { clearFormDraft(); navigate(routes.leaveEncashment.list); }}>{t("cancel")}</button>
                <button type="button" className="btn btn-danger" onClick={() => { if (!validate()) return; saveFormDraft(form); navigate(routes.leaveEncashment.review, { state: { formData: form } }); }}>{t("next")}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateLeaveEncashment;
